import React from "react";
import { Document, Page, Text, View, StyleSheet, Link } from "@react-pdf/renderer";

const styles = StyleSheet.create({
  page: {
    backgroundColor: "#ffffff",
    color: "#101010",
    fontFamily: "Times-Roman",
    fontSize: 10.5,
    paddingHorizontal: 56,
    paddingVertical: 48,
    lineHeight: 1.45,
  },
  center: { alignItems: "center", marginBottom: 22, borderBottomWidth: 0.7, borderBottomColor: "#101010", paddingBottom: 18 },
  name: { fontSize: 30, fontFamily: "Times-Bold", letterSpacing: 4, textAlign: "center" },
  role: { fontSize: 11, fontFamily: "Times-Italic", color: "#3a3a3a", marginTop: 4, textAlign: "center" },
  meta: { flexDirection: "row", justifyContent: "center", marginTop: 8, fontSize: 9.5, color: "#3a3a3a" },
  metaItem: { marginHorizontal: 5 },
  sep: { color: "#7a7a7a", marginHorizontal: 5 },
  sectionTitle: {
    fontSize: 11,
    fontFamily: "Times-Bold",
    letterSpacing: 4,
    marginTop: 16,
    marginBottom: 6,
    borderBottomWidth: 0.4,
    borderBottomColor: "#2a2a2a",
    paddingBottom: 4,
  },
  entry: { marginTop: 8 },
  entryHead: { flexDirection: "row", justifyContent: "space-between", alignItems: "baseline" },
  entryTitle: { fontSize: 11.5, fontFamily: "Times-Bold" },
  entrySpan: { fontSize: 9.5, fontFamily: "Times-Italic", color: "#3a3a3a" },
  entrySub: { fontSize: 10, fontFamily: "Times-Italic", color: "#404040", marginTop: 1 },
  entryBody: { fontSize: 10.2, color: "#1a1a1a", marginTop: 3, lineHeight: 1.5 },
  bullet: { flexDirection: "row", marginTop: 1.5 },
  bulletMark: { width: 10, fontSize: 10 },
  bulletText: { flex: 1, fontSize: 10, color: "#1a1a1a" },
  quote: { fontFamily: "Times-Italic", fontSize: 11, textAlign: "center", color: "#2a2a2a", marginTop: 4, marginBottom: 4 },
  skillLine: { fontSize: 10, marginTop: 3, color: "#1a1a1a" },
  footer: {
    position: "absolute",
    bottom: 26,
    left: 56,
    right: 56,
    textAlign: "center",
    fontSize: 8,
    color: "#7a7a7a",
    fontFamily: "Times-Italic",
  },
});

const ClassicExecutive = ({ profile, credentials, awards, experiences, projects, skills }) => {
  const contactBits = [profile.location, profile.email, profile.phone].filter(Boolean);

  return (
    <Document
      author={profile.name}
      title={`${profile.name} — Resume`}
      subject="Curriculum Vitae"
    >
      <Page size="A4" style={styles.page}>
        <View style={styles.center}>
          <Text style={styles.name}>{profile.name}</Text>
          <Text style={styles.role}>{profile.titles?.join(" · ") || "Entrepreneur · Investor · Business Strategist"}</Text>
          <View style={styles.meta}>
            {contactBits.map((b, i) => (
              <React.Fragment key={i}>
                {i > 0 && <Text style={styles.sep}>·</Text>}
                <Text style={styles.metaItem}>{b}</Text>
              </React.Fragment>
            ))}
            {profile.linkedin && (
              <>
                <Text style={styles.sep}>·</Text>
                <Link src={profile.linkedin} style={{ ...styles.metaItem, color: "#101010", textDecoration: "underline" }}>LinkedIn</Link>
              </>
            )}
            <Text style={styles.sep}>·</Text>
            <Link src="https://utkarshbokadia.com" style={{ ...styles.metaItem, color: "#101010", textDecoration: "underline" }}>utkarshbokadia.com</Link>
          </View>
        </View>

        {profile.summary && <Text style={styles.quote}>&ldquo;{profile.summary}&rdquo;</Text>}

        {/* EXPERIENCE */}
        <Text style={styles.sectionTitle}>EXPERIENCE</Text>
        {experiences.slice(0, 7).map((e) => (
          <View key={e.id} style={styles.entry} wrap={false}>
            <View style={styles.entryHead}>
              <Text style={styles.entryTitle}>{e.company}{e.current ? " — Current" : ""}</Text>
              <Text style={styles.entrySpan}>{e.span}</Text>
            </View>
            <Text style={styles.entrySub}>{e.role} — {e.location}</Text>
            {e.summary && <Text style={styles.entryBody}>{e.summary}</Text>}
            {Array.isArray(e.achievements) && e.achievements.slice(0, 4).map((a, i) => (
              <View key={i} style={styles.bullet}>
                <Text style={styles.bulletMark}>·</Text>
                <Text style={styles.bulletText}>{a}</Text>
              </View>
            ))}
          </View>
        ))}

        {/* PROJECTS */}
        {projects.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>SELECTED WORK</Text>
            {projects.slice(0, 4).map((p) => (
              <View key={p.id} style={styles.entry} wrap={false}>
                <View style={styles.entryHead}>
                  <Text style={styles.entryTitle}>{p.name}</Text>
                  <Text style={styles.entrySpan}>{p.year}</Text>
                </View>
                <Text style={styles.entrySub}>{p.tagline}{p.displayUrl ? ` — ${p.displayUrl}` : ""}</Text>
                {p.mission && <Text style={styles.entryBody}>{p.mission}</Text>}
              </View>
            ))}
          </>
        )}
      </Page>

      <Page size="A4" style={styles.page}>
        <Text style={styles.sectionTitle}>CREDENTIALS & CERTIFICATIONS</Text>
        {credentials.slice(0, 16).map((c) => (
          <View key={c.id} style={styles.entry} wrap={false}>
            <View style={styles.entryHead}>
              <Text style={styles.entryTitle}>{c.title}</Text>
              <Text style={styles.entrySpan}>{c.issued}</Text>
            </View>
            <Text style={styles.entrySub}>{c.issuer}{c.credentialId ? ` — ID ${c.credentialId}` : ""}</Text>
          </View>
        ))}

        {awards.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>AWARDS & DISTINCTIONS</Text>
            {awards.slice(0, 8).map((a) => (
              <View key={a.id} style={styles.entry} wrap={false}>
                <View style={styles.entryHead}>
                  <Text style={styles.entryTitle}>{a.title}</Text>
                  <Text style={styles.entrySpan}>{a.year || a.date}</Text>
                </View>
                {a.subtitle && <Text style={styles.entrySub}>{a.subtitle}{a.org ? ` — ${a.org}` : ""}</Text>}
              </View>
            ))}
          </>
        )}

        {skills.length > 0 && (
          <>
            <Text style={styles.sectionTitle}>Skills</Text>
            <Text style={styles.skillLine}>{skills.map((s) => s.name).join(" · ")}</Text>
          </>
        )}

        <Text style={styles.footer} fixed>
          {profile.name} — utkarshbokadia.com
        </Text>
      </Page>
    </Document>
  );
};

export default ClassicExecutive;
